<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import intlTelInput, { type Iti } from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import FieldLabel from '@/shared/ui/container/FieldLabel.vue';

const props = withDefaults(
    defineProps<{
        label: string;
        ariaLabel?: string;
        errorMessages?: string | string[];
        disabled?: boolean;
    }>(),
    {
        disabled: false,
    }
);

const phoneNumber = defineModel<string | undefined>('phoneNumber', { default: '' });
const countryName = defineModel<string | undefined>('countryName', { default: '' });
const isPhoneValid = defineModel<boolean>('isPhoneValid', { default: true });

const inputRef = ref<HTMLInputElement | null>(null);
const itiInstance = ref<Iti | null>(null);

const hasError = computed(() => {
    if (!props.errorMessages) return false;
    if (Array.isArray(props.errorMessages)) return props.errorMessages.length > 0;
    return props.errorMessages.length > 0;
});

const errorMessageText = computed(() => {
    if (!props.errorMessages) return '';
    if (Array.isArray(props.errorMessages)) return props.errorMessages[0] || '';
    return props.errorMessages;
});

const updatePhoneAndCountry = () => {
    if (!itiInstance.value || !inputRef.value) return;

    const countryData = itiInstance.value.getSelectedCountryData();
    countryName.value = countryData.name || '';

    // 入力欄が空の場合は電話番号も空として扱う（任意項目）
    const inputValue = inputRef.value.value.trim();
    if (!inputValue) {
        phoneNumber.value = '';
        isPhoneValid.value = true;
        return;
    }

    // E.164形式の番号を取得
    // separateDialCode: true の場合、入力フィールドにはダイヤルコードなしの番号が入る
    // getNumber() は完全なE.164形式を返す
    let number = itiInstance.value.getNumber();

    // getNumber()が空でも入力値がある場合は、ダイヤルコード + 入力値を組み立てる
    if (!number) {
        const dialCode = countryData.dialCode ? `+${countryData.dialCode}` : '';
        // ハイフン、スペース、括弧などを除去してE.164形式に正規化
        const cleanedInput = inputValue.replace(/[\s\-()]/g, '');
        number = dialCode + cleanedInput;
    }

    phoneNumber.value = number || '';

    // 電話番号の有効性をチェック
    // ハイフン、スペース、括弧を除去した数字のみで検証
    // ※ この結果は親コンポーネント経由で schema.ts の isPhoneValid バリデーションで確認される
    // ※ 関連: application-input/model/schema.ts, application-edit/model/schema.ts
    // 数字と+のみを抽出（E.164形式: +819012345678）
    const digitsOnly = phoneNumber.value.replace(/[^\d+]/g, '');
    // 最低限の桁数チェック（国番号 + 数桁の番号）
    // 例: +81 90-1234-5678 → +819012345678 (12桁)
    const hasValidLength = digitsOnly.length >= 8 && digitsOnly.length <= 16;
    const startsWithPlus = digitsOnly.startsWith('+');
    isPhoneValid.value = hasValidLength && startsWithPlus;
};

const onCountryChange = () => {
    updatePhoneAndCountry();
};

const onInput = () => {
    if (inputRef.value) {
        // 許可する文字: 数字、+、-、スペース、括弧のみ（ひらがな・記号等を除去）
        const filtered = inputRef.value.value.replace(/[^\d+\-\s()]/g, '');
        if (filtered !== inputRef.value.value) {
            inputRef.value.value = filtered;
        }
    }
    updatePhoneAndCountry();
};

const initIntlTelInput = async () => {
    if (!inputRef.value) return;

    // ジオロケーションで初期国を自動選択
    let initialCountry = 'jp';
    try {
        const response = await fetch('https://ipapi.co/country/');
        if (response.ok) {
            const countryCode = await response.text();
            if (countryCode && countryCode.length === 2) {
                initialCountry = countryCode.toLowerCase();
            }
        }
    } catch {
        // フォールバック: 日本
    }

    // fetch完了までにコンポーネントがアンマウントされた場合は中断
    if (!inputRef.value) return;

    itiInstance.value = intlTelInput(inputRef.value, {
        initialCountry: initialCountry as 'jp',
        countryOrder: ['jp', 'us', 'gb', 'cn', 'kr', 'tw', 'th', 'vn', 'ph', 'sg'],
        separateDialCode: true,
        nationalMode: false,
        formatOnDisplay: true,
        dropdownContainer: document.body,
    });

    // 初期値を設定
    if (phoneNumber.value) {
        itiInstance.value.setNumber(phoneNumber.value);
    }

    // 初期の国名を設定
    const countryData = itiInstance.value.getSelectedCountryData();
    if (countryData.name) {
        countryName.value = countryData.name;
    }

    inputRef.value.addEventListener('countrychange', onCountryChange);
    inputRef.value.addEventListener('input', onInput);
};

onMounted(async () => {
    await initIntlTelInput();
});

onUnmounted(() => {
    if (inputRef.value) {
        inputRef.value.removeEventListener('countrychange', onCountryChange);
        inputRef.value.removeEventListener('input', onInput);
    }
    if (itiInstance.value) {
        itiInstance.value.destroy();
    }
});

// 外部から電話番号が変更された場合に反映
watch(
    () => phoneNumber.value,
    (newValue) => {
        if (itiInstance.value && newValue && newValue !== itiInstance.value.getNumber()) {
            itiInstance.value.setNumber(newValue);
        }
    }
);
</script>

<template>
    <div class="intl-tel-input-wrapper">
        <FieldLabel :label="label" />
        <input
            ref="inputRef"
            type="tel"
            inputmode="tel"
            class="intl-tel-input-field"
            :class="{ 'has-error': hasError }"
            :aria-label="ariaLabel || label"
            :disabled="disabled"
        />
        <div v-if="hasError" class="error-message">
            {{ errorMessageText }}
        </div>
    </div>
</template>

<style scoped>
.intl-tel-input-wrapper {
    width: 100%;
}

.intl-tel-input-wrapper :deep(.iti) {
    width: 100%;
}

.intl-tel-input-field {
    width: 100%;
    height: 40px;
    padding: 8px 12px;
    font-size: 14px;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;
    outline: none;
    transition: border-color 0.2s;
}

.intl-tel-input-field:focus {
    border-color: rgb(var(--v-theme-primary));
}

.intl-tel-input-field.has-error {
    border-color: rgb(var(--v-theme-error));
}

.intl-tel-input-field:disabled {
    background-color: rgba(var(--v-theme-surface), 0.5);
    cursor: not-allowed;
}

.error-message {
    color: rgb(var(--v-theme-error));
    font-size: 12px;
    margin-top: 4px;
}
</style>
